import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
}

interface NotificationContextType {
  showNotification: (type: NotificationType, message: string, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback(
    (type: NotificationType, message: string, duration = 4000) => {
      const id = Math.random().toString(36).substring(7);
      const notification: Notification = { id, type, message, duration };

      setNotifications((prev) => [...prev, notification]);

      if (duration > 0) {
        setTimeout(() => removeNotification(id), duration);
      }
    },
    [removeNotification]
  );

  const success = useCallback(
    (message: string, duration?: number) => showNotification('success', message, duration),
    [showNotification]
  );

  const error = useCallback(
    (message: string, duration?: number) => showNotification('error', message, duration),
    [showNotification]
  );

  const info = useCallback(
    (message: string, duration?: number) => showNotification('info', message, duration),
    [showNotification]
  );

  const warning = useCallback(
    (message: string, duration?: number) => showNotification('warning', message, duration),
    [showNotification]
  );

  const getNotificationStyles = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return 'bg-green-600/90 border-green-500';
      case 'error':
        return 'bg-red-600/90 border-red-500';
      case 'warning':
        return 'bg-orange-600/90 border-orange-500';
      case 'info':
        return 'bg-neon-cyan/90 border-neon-cyan';
      default:
        return 'bg-gray-600/90 border-gray-500';
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification, success, error, info, warning }}>
      {children}

      {/* Notification Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        <AnimatePresence>
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: 100, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.8 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`
                ${getNotificationStyles(notification.type)}
                backdrop-blur-sm rounded-lg border-2 px-6 py-4 shadow-2xl
                pointer-events-auto max-w-md min-w-[300px]
              `}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {notification.type === 'success' && <span className="text-2xl">✓</span>}
                  {notification.type === 'error' && <span className="text-2xl">✕</span>}
                  {notification.type === 'warning' && <span className="text-2xl">⚠</span>}
                  {notification.type === 'info' && <span className="text-2xl">ℹ</span>}
                </div>
                <p className="font-roboto text-white text-sm font-medium flex-1">
                  {notification.message}
                </p>
                <button
                  onClick={() => removeNotification(notification.id)}
                  className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
